import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Stack
} from "@mui/material";

import {
    Groups,
    Person,
    VolunteerActivism,
    School,
    Download
} from "@mui/icons-material";

import Swal from "sweetalert2";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";
import { pdf } from "@react-pdf/renderer";
import StudentStatusSummaryPDF from "./StudentStatusSummaryPDF";


// IMPORT YOUR LOCAL SCHOOL LOGO
import schoolLogo from "./zpa.png";


const StudentStatusSummary = () => {

    const {
        selectedYear,
        loading: academicYearLoading
    } = useAcademicYear();


    const [summaryData, setSummaryData] = useState(null);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const [selectedGradeClass, setSelectedGradeClass] = useState("");


    /*
    |--------------------------------------------------------------------------
    | FETCH STUDENT STATUS SUMMARY
    |--------------------------------------------------------------------------
    */

    const fetchStudentStatusSummary = useCallback(async () => {

        if (!selectedYear) {
            return;
        }


        try {

            setLoading(true);

            setError("");


            const response = await api.get(
                "/student-status-summary/"
            );


            setSummaryData(response.data);


        } catch (err) {

            console.error(
                "Failed to fetch student status summary:",
                err
            );


            const message =
                err.response?.data?.detail ||
                "Failed to load student status summary.";


            setError(message);


            setSummaryData(null);


        } finally {

            setLoading(false);

        }

    }, [selectedYear]);


    /*
    |--------------------------------------------------------------------------
    | FETCH WHEN SELECTED YEAR CHANGES
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchStudentStatusSummary();

    }, [
        fetchStudentStatusSummary,
        selectedYear
    ]);


    /*
    |--------------------------------------------------------------------------
    | GRADE CLASS OPTIONS
    |--------------------------------------------------------------------------
    */

    const gradeClasses = useMemo(() => {

        return summaryData?.grade_classes || [];

    }, [summaryData]);


    /*
    |--------------------------------------------------------------------------
    | SELECTED GRADE CLASS
    |--------------------------------------------------------------------------
    */

    const selectedClassData = useMemo(() => {

        return gradeClasses.find(
            item =>
                item.grade_class === selectedGradeClass
        );

    }, [
        gradeClasses,
        selectedGradeClass
    ]);


    /*
    |--------------------------------------------------------------------------
    | DOWNLOAD PDF
    |--------------------------------------------------------------------------
    */

    const handleDownloadStatistics = async () => {

        if (!selectedClassData) {

            Swal.fire(
                "Select Grade Class",
                "Please select a grade class before downloading the statistics.",
                "warning"
            );

            return;

        }


        try {

            Swal.fire({

                title: "Preparing PDF...",

                text: "Generating student statistics report.",

                allowOutsideClick: false,

                didOpen: () => {

                    Swal.showLoading();

                }

            });


            const blob = await pdf(

                <StudentStatusSummaryPDF

                    schoolName="Excellent Kids Foundation"

                    logo={schoolLogo}

                    academicYear={
                        summaryData?.academic_year ||
                        selectedYear
                    }

                    gradeClass={selectedClassData}

                />

            ).toBlob();


            const url = window.URL.createObjectURL(blob);


            const link = document.createElement("a");

            link.href = url;


            const safeGradeClass =
                selectedClassData.grade_class
                    .replace(
                        /[^a-z0-9]/gi,
                        "_"
                    );


            const academicYearName =
                summaryData?.academic_year?.name ||
                selectedYear?.name ||
                "Academic_Year";


            link.download =
                `Student_Status_Statistics_${safeGradeClass}_${academicYearName}.pdf`;


            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);


            window.URL.revokeObjectURL(url);


            Swal.close();


        } catch (err) {

            console.error(
                "PDF generation failed:",
                err
            );


            Swal.close();


            Swal.fire(
                "Download Failed",
                "Unable to generate the student statistics PDF.",
                "error"
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (academicYearLoading || loading) {

        return (

            <Box

                sx={{

                    minHeight: "400px",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center"

                }}

            >

                <CircularProgress />

            </Box>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | NO ACTIVE YEAR
    |--------------------------------------------------------------------------
    */

    if (!selectedYear) {

        return (

            <Box sx={{ p: 3 }}>

                <Alert severity="warning">

                    No active academic year is currently selected.

                </Alert>

            </Box>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    if (error) {

        return (

            <Box sx={{ p: 3 }}>

                <Alert severity="error">

                    {error}

                </Alert>

            </Box>

        );

    }


    const summary =
        summaryData?.summary || {};


    return (

        <Box

            sx={{

                p: {

                    xs: 2,

                    md: 3

                },

                backgroundColor: "#f5f6f8",

                minHeight: "100vh"

            }}

        >

            {/* HEADER */}

            <Box

                sx={{

                    mb: 3

                }}

            >

                <Typography

                    variant="h4"

                    sx={{

                        fontWeight: 800,

                        color: "#1b1617",

                        mb: 0.5

                    }}

                >

                    Student Status Summary

                </Typography>


                <Typography

                    variant="body1"

                    color="text.secondary"

                >

                    Academic Year:{" "}

                    <strong>

                        {summaryData?.academic_year?.name ||
                            selectedYear.name}

                    </strong>

                </Typography>

            </Box>


            {/* SUMMARY CARDS */}

            <Grid

                container

                spacing={2}

                sx={{ mb: 4 }}

            >

                <Grid item xs={12} sm={6} md={3}>

                    <SummaryCard

                        title="Total Students"

                        value={summary.total_students || 0}

                        icon={<Groups />}

                        color="#5b0018"

                    />

                </Grid>


                <Grid item xs={12} sm={6} md={3}>

                    <SummaryCard

                        title="Regular Students"

                        value={summary.regular || 0}

                        icon={<Person />}

                        color="#1565c0"

                    />

                </Grid>


                <Grid item xs={12} sm={6} md={3}>

                    <SummaryCard

                        title="Dependent Students"

                        value={summary.dependent || 0}

                        icon={<VolunteerActivism />}

                        color="#ef6c00"

                    />

                </Grid>


                <Grid item xs={12} sm={6} md={3}>

                    <SummaryCard

                        title="Scholarship Students"

                        value={summary.scholarship || 0}

                        icon={<School />}

                        color="#2e7d32"

                    />

                </Grid>

            </Grid>


            {/* DOWNLOAD SECTION */}

            <Card

                elevation={0}

                sx={{

                    borderRadius: 3,

                    border: "1px solid #e1e4e8",

                    backgroundColor: "#ffffff"

                }}

            >

                <CardContent sx={{ p: 3 }}>

                    <Stack

                        direction={{

                            xs: "column",

                            md: "row"

                        }}

                        spacing={2}

                        alignItems={{

                            xs: "stretch",

                            md: "center"

                        }}

                        justifyContent="space-between"

                    >

                        <Box>

                            <Typography

                                variant="h6"

                                sx={{

                                    fontWeight: 700,

                                    color: "#333"

                                }}

                            >

                                Download Class Statistics

                            </Typography>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                            >

                                Select a grade class to download a detailed alphabetical student status report.

                            </Typography>

                        </Box>


                        <FormControl

                            sx={{

                                minWidth: {

                                    xs: "100%",

                                    md: 280

                                }

                            }}

                        >

                            <InputLabel>

                                Select Grade Class

                            </InputLabel>


                            <Select

                                value={selectedGradeClass}

                                label="Select Grade Class"

                                onChange={(event) => {

                                    setSelectedGradeClass(
                                        event.target.value
                                    );

                                }}

                            >

                                <MenuItem value="">

                                    <em>

                                        Select Grade Class

                                    </em>

                                </MenuItem>


                                {gradeClasses.map(

                                    (gradeClass) => (

                                        <MenuItem

                                            key={
                                                gradeClass.grade_class
                                            }

                                            value={
                                                gradeClass.grade_class
                                            }

                                        >

                                            {gradeClass.grade_class}

                                            {" - "}

                                            {gradeClass.total_students}

                                            {" Students"}

                                        </MenuItem>

                                    )

                                )}

                            </Select>

                        </FormControl>


                        <Button

                            variant="contained"

                            startIcon={<Download />}

                            onClick={
                                handleDownloadStatistics
                            }

                            disabled={!selectedClassData}

                            sx={{

                                minHeight: 52,

                                px: 3,

                                fontWeight: 700,

                                backgroundColor: "#e77f00",

                                "&:hover": {

                                    backgroundColor: "#000000"

                                }

                            }}

                        >

                            Download Statistics

                        </Button>

                    </Stack>


                    {selectedClassData && (

                        <>

                            <Divider sx={{ my: 3 }} />


                            <Grid

                                container

                                spacing={2}

                            >

                                <Grid

                                    item

                                    xs={12}

                                    sm={6}

                                    md={3}

                                >

                                    <ClassStat

                                        label="Class Total"

                                        value={
                                            selectedClassData.total_students
                                        }

                                    />

                                </Grid>


                                <Grid

                                    item

                                    xs={12}

                                    sm={6}

                                    md={3}

                                >

                                    <ClassStat

                                        label="Regular"

                                        value={
                                            selectedClassData.regular_count
                                        }

                                    />

                                </Grid>


                                <Grid

                                    item

                                    xs={12}

                                    sm={6}

                                    md={3}

                                >

                                    <ClassStat

                                        label="Dependent"

                                        value={
                                            selectedClassData.dependent_count
                                        }

                                    />

                                </Grid>


                                <Grid

                                    item

                                    xs={12}

                                    sm={6}

                                    md={3}

                                >

                                    <ClassStat

                                        label="Scholarship"

                                        value={
                                            selectedClassData.scholarship_count
                                        }

                                    />

                                </Grid>

                            </Grid>

                        </>

                    )}

                </CardContent>

            </Card>

        </Box>

    );

};


const SummaryCard = ({
    title,
    value,
    icon,
    color
}) => {

    return (

        <Card

            elevation={0}

            sx={{

                borderRadius: 3,

                border: "1px solid #e1e4e8",

                backgroundColor: "#ffffff"

            }}

        >

            <CardContent>

                <Stack

                    direction="row"

                    alignItems="center"

                    justifyContent="space-between"

                >

                    <Box>

                        <Typography

                            variant="body2"

                            color="text.secondary"

                        >

                            {title}

                        </Typography>


                        <Typography

                            variant="h4"

                            sx={{

                                mt: 1,

                                fontWeight: 800,

                                color

                            }}

                        >

                            {value}

                        </Typography>

                    </Box>


                    <Box

                        sx={{

                            width: 52,

                            height: 52,

                            borderRadius: "50%",

                            display: "flex",

                            alignItems: "center",

                            justifyContent: "center",

                            backgroundColor: `${color}15`,

                            color

                        }}

                    >

                        {icon}

                    </Box>

                </Stack>

            </CardContent>

        </Card>

    );

};


const ClassStat = ({
    label,
    value
}) => {

    return (

        <Box

            sx={{

                p: 2,

                borderRadius: 2,

                backgroundColor: "#f7f8fa",

                border: "1px solid #e8eaed"

            }}

        >

            <Typography

                variant="body2"

                color="text.secondary"

            >

                {label}

            </Typography>


            <Typography

                variant="h5"

                sx={{

                    fontWeight: 800,

                    color: "#5b0018",

                    mt: 0.5

                }}

            >

                {value || 0}

            </Typography>

        </Box>

    );

};


export default StudentStatusSummary;